const Grey2 = '#F0F0F0'

const colors = {
  Transparent: 'transparent',
  White: '#FFFFFF',
  Grey1: '#F4F4F4',
  Grey2,
  Grey4: '#E7E8E8',
  Grey5: '#DEDEDE',
  // TODO: rename to Grey7
  Gray7: '#C8C7C7',
  Gray8: '#ADADAD',
  // TODO: rename to Grey9
  Gray9: '#585858',
  Grey10: '#2C343E',
  Black: '#000000',
  GreenPrimary: '#5AB031',
  GreenDark: '#468826',
  GreenDarkest: '#1E7E34',
  GreenLight: '#4BC212',
  Green25: '#D6EBCB',
  Green65: '#94CC79',
  GreenBright: '#D0FBB8',
  GreenBrighter: '#E6FADB',
  Red: '#D34342',
  RedBright: '#FF4400',
  Pink: '#FFE0E0',
  Yellow: '#FFC632',
  breadcrumbPath: '#9D9D9D',
  menuShadow: Grey2,
  // From here on - new design
  MainYellow: '#FFD249',
  MainYellowOnHover: '#F9BF11',
  
  MainBlack: '#242424',
  DarkGrey: '#ACACAC',
  LightGrey: '#F3F3F3',
  GreyScaleBackground: '#FBFBFB',
  SecondaryLightGrey: '#D9D9D9',
  SemiDarkGrey: '#A2A2A2',
  LightYellow: '#FFE9A4',
  TransparentYellow: '#FFFCF1',

  SecondaryGrey: '#595959',
  MediumGrey: '#E3E3E3',
  SecondaryDarkGrey: '#3F3E41',
  LightBackground: '#FBFBFB',
  SecondaryDarkYellow: '#DDB03C',
  LightGreen: '#C1EEAC',
  LightRed: '#FFA3A3',
  SecondaryBrown: '#C25700',

  RpicRed: '#FF1616',
  VeryLightYellow: '#FFF9E3',
  VeryLightRed: '#FFECEC',
  LaunchpadAColor: '#48DEFF',
  LaunchpadCColor: '#BD00FF',
  LaunchpadGColor: '#FF9A03',
  LaunchpadHColor: '#3661FA',

  AvatarColorLightRed: '#EF9A9A',
  AvatarColorPink: '#F48FB1',
  AvatarColorLightPurple: '#CE93D8',
  AvatarColorPurple: '#B39DDB',
  AvatarColorDarkPurple: '#9FA8DA', 
  AvatarColorBlue: '#90CAF9',
  AvatarColorLightBlue: '#B3E5FC',
  AvatarColorTurquoise: '#80DEEA', 
  AvatarColorDarkTurquoise: '#80CBC4',
  AvatarColorDarkGreen: '#A5D6A7',
  AvatarColorGreen: '#C5E1A5',
  AvatarColorLightGreen: '#E6EE9C',
  AvatarColorYellow: '#FFF59D',
  AvatarColorDarkYellow: '#FFE082',
  AvatarColorOrange: '#FFCC80',
  AvatarColorDarkOrange: '#FFAB91',
  AvatarColorBrown: '#BCAAA4',
  AvatarColorGray: '#B0BEC5',

  SecondaryDeliveredGreen: '#E8F6E9',
  ExtendedOrangeBackgroundOrange: '#FDE4CE',

} as const

// https://stackoverflow.com/a/53662389/915875
export type ColorName = keyof typeof colors
export type ColorValue = typeof colors[ColorName]

const fonts = {
  body: "'SF-Pro', 'Open Sans', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif",
  buttonsAndTitlesFont:
    "'PPRightGrotesk', 'Open Sans', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif",
} as const

const fontSizes = {
  tiny: '10px',
  small: '12px',
  smaller: '14px',
  medium: '16px',
  big: '18px',
  huge: '24px',
} as const

const fontWeights = {
  regular: 400,
  bold: 700,
} as const

const lineHeights = {
  normal: 1.2,
} as const

const borderWidths = {
  thin: '1px',
  medium: '3px',
  thick: 'thick',
} as const

const borders = {
  menu: `${borderWidths.thin} solid ${colors.Grey5}`,
  select: `${borderWidths.thin} solid ${colors.DarkGrey}`,
  table: `${borderWidths.thin} solid ${colors.Grey4}`,
  menuSeparator: `${borderWidths.thin} solid ${colors.Grey2}`,
  tabGroup: `${borderWidths.thin} solid ${colors.MediumGrey}`,
  formStep: `${borderWidths.thin} solid ${colors.DarkGrey}`,
  card: `${borderWidths.thin} solid ${colors.DarkGrey}`,
  tabLinkContainer: `${borderWidths.medium} solid ${colors.LightGrey}`,
} as const

const radii = {
  small: '3px',
  tooltipRadii: '5px',
  medium: '10px',
  large: '2em',
} as const

const shadows = {
  menu: `0 1px 4px 0 ${colors.menuShadow}`,
} as const

const transitions = {
  normal: '0.3s',
} as const

const breakpoints = {
  small: {
    max: '767px',
  },
  medium: {
    min: '768px',
    max: '1023px',
  },
  big: {
    min: '1024px',
  },
} as const

// The values above are the canonical ones and are exposed via the `theme` below. They should never be exported directly.

/** Theme schema is based on the system-ui theme specification https://system-ui.com/theme */
export const theme = {
  colors: {
    ...colors,
    body: colors.MainBlack,
    bodyHover: colors.Black,
    bodyBg: colors.White,
    navbarBg: colors.LightGrey,
    warning: colors.Yellow,
    recordHover: colors.LightGrey,
    recordSelected: colors.TransparentYellow,
    recordHoverBorder: colors.GreenPrimary,
    danger: colors.Red,
    dangerBg: colors.Pink,
  },
  svgFilterColors: {
    MainYellow:
      'brightness(0) saturate(100%) invert(92%) sepia(40%) saturate(3241%) hue-rotate(318deg) brightness(103%) contrast(102%)',
  },
  fonts,
  fontSizes: {
    ...fontSizes,
    body: fontSizes.medium,
    label: fontSizes.small,
    value: fontSizes.small,
    h1: fontSizes.huge,
    h2: fontSizes.medium,
    h3: fontSizes.small,
    vehicle: fontSizes.huge,
  },
  fontWeights,
  lineHeights,
  borderWidths,
  borders,
  radii,
  shadows,
  transitions,
  breakpoints,
} as const
